/**
 * Kickard PHASE 5.0B — Play Hub Premium (approved exact V2).
 *
 * Presentation-only rebuild of the Play Hub against the APPROVED V2
 * master reference. Every click handler, matchmaking flow, Ranked
 * route, Friend room create/join behavior, Bot route and Back
 * behavior is PRESERVED BYTE-FOR-BYTE from the Pass 1C build; only
 * the markup/styling is new.
 *
 * Visual source of truth:
 *   /public/kickard/play-hub/cards/*.webp   — mode card textless art
 *   /public/kickard/play-hub/icons/*.png    — expanded-Friend sub-icons
 *   /public/home-stadium.webp               — reused Home stadium
 *   /public/kickard-logo.webp               — reused production logo
 *
 * HARD LOCKS:
 *   • Friend Match stays INLINE on the same screen (no new route).
 *   • Quick Match launches the SAME MatchmakingScreen flow.
 *   • Ranked route unchanged (/ranked/search).
 *   • Bot route unchanged (/play).
 *   • Create Room / Join Room / room-code input unchanged.
 *   • Back behavior unchanged.
 *   • No fake OS status/nav bars — real safe-area insets only.
 */
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PremiumBackButton, PremiumHomeButton } from '@/components/PremiumNav';
import { ensureGuest, loadGuest } from '@/lib/guestIdentity';
import { track, EVT } from '@/lib/telemetry';
import { setScreen } from '@/lib/sessionTracking';
import { getRankedAvailability } from '@/api/rankedClient';
import { useOnlineActions } from '@/hooks/useOnlineActions';
import { saveOnlineSession } from '@/net/onlineSession';
import MatchmakingScreen from '@/screens/MatchmakingScreen';

const STADIUM_ASSET = '/home-stadium.webp';
const LOGO_ASSET = '/kickard-logo.webp';
const CARD = {
  quick:  '/kickard/play-hub/cards/quick_approved_textless_2x.webp',
  ranked: '/kickard/play-hub/cards/ranked_approved_textless_2x.webp',
  friend: '/kickard/play-hub/cards/friend_approved_textless_2x.webp',
  bot:    '/kickard/play-hub/cards/bot_approved_textless_2x.webp',
};
const ICON = {
  create:   '/kickard/play-hub/icons/create_room_approved.png',
  code:     '/kickard/play-hub/icons/room_code_approved.png',
  join:     '/kickard/play-hub/icons/join_room_approved.png',
};

export default function PlayHubScreen({ onBack }) {
  const navigate = useNavigate();
  const [rankedAvail, setRankedAvail] = useState(null);
  const [friendExpanded, setFriendExpanded] = useState(false);
  const [codeOpen, setCodeOpen] = useState(false);
  const [joinCode, setJoinCode] = useState('');
  const [searching, setSearching] = useState(false);
  const codeInputRef = useRef(null);
  const actions = useOnlineActions();

  useEffect(() => {
    setScreen('play_hub');
    ensureGuest();
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const a = await getRankedAvailability();
        if (!cancelled) setRankedAvail(a);
      } catch (_) { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, []);

  const rankedAvailabilityKnown = rankedAvail?.ok === true;
  const rankedEnabled = !!(rankedAvailabilityKnown
                          && rankedAvail.enabled
                          && rankedAvail.activeSeasonExists);
  // PHASE 5.0B — Astrix repro: Ranked is only visually "closed" when
  // the server authoritatively confirms it. LOADING (rankedAvail null)
  // and ERROR (ok:false) must NOT disable Ranked or render OPENS SOON.
  const rankedClosedExplicit = rankedAvailabilityKnown && !rankedEnabled;

  function _handoffToOnline(session) {
    if (!session) return;
    saveOnlineSession(session);
    navigate('/online');
  }

  function onQuickTap() {
    track(EVT.HOME_CTA_CLICKED, { metadata: { cta: 'play_hub_quick' } });
    actions.clearError();
    setSearching(true);
  }
  function onRankedTap() {
    // Allow entry whenever Ranked is NOT authoritatively closed —
    // consistent with Home. The downstream /ranked/search screen
    // handles any later matchmaking error with its own retry UX.
    if (rankedClosedExplicit) return;
    track(EVT.HOME_CTA_CLICKED, { metadata: { cta: 'play_hub_ranked' } });
    navigate('/ranked/search');
  }
  async function onFriendCreate() {
    track(EVT.HOME_CTA_CLICKED, { metadata: { cta: 'play_hub_friend_create' } });
    const session = await actions.createRoom();
    _handoffToOnline(session);
  }
  async function onFriendJoin() {
    if (joinCode.length !== 6 || actions.busy) return;
    track(EVT.HOME_CTA_CLICKED, { metadata: { cta: 'play_hub_friend_join' } });
    const session = await actions.joinRoom(joinCode);
    _handoffToOnline(session);
  }
  function onBotTap() {
    track(EVT.HOME_CTA_CLICKED, { metadata: { cta: 'play_hub_bot' } });
    track(EVT.BOT_MATCH_STARTED, { matchType: 'bot_practice' });
    navigate('/play');
  }

  // Tapping ENTER ROOM CODE reveals the real input inline + focuses it.
  function onCodeTap() {
    setCodeOpen((v) => !v);
    track(EVT.HOME_CTA_CLICKED, { metadata: { cta: 'play_hub_friend_code' } });
    // defer focus until the input is rendered
    requestAnimationFrame(() => codeInputRef.current?.focus?.());
  }

  if (searching) {
    const guest = loadGuest();
    return (
      <MatchmakingScreen
        guest={guest}
        onMatched={(s) => {
          setSearching(false);
          _handoffToOnline({
            roomCode: s.roomCode,
            playerId: s.playerId,
            token: s.token,
            name: s.name,
            opponentName: s.opponentName,
          });
        }}
        onCancel={() => setSearching(false)}
        onPlayBot={() => { setSearching(false); navigate('/play'); }}
      />
    );
  }

  return (
    <div
      data-testid="play-hub"
      className="relative w-full min-h-[100dvh] overflow-x-hidden text-white"
      style={{
        maxWidth: 430,
        marginLeft: 'auto',
        marginRight: 'auto',
        paddingTop: 'env(safe-area-inset-top)',
        paddingBottom: 'calc(env(safe-area-inset-bottom) + 24px)',
        paddingLeft: 'env(safe-area-inset-left)',
        paddingRight: 'env(safe-area-inset-right)',
      }}
    >
      {/* Stadium background — reused directly from the production Home. */}
      <div
        aria-hidden
        className="absolute inset-0 z-0"
        style={{
          backgroundImage: `url(${STADIUM_ASSET})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center top',
          backgroundRepeat: 'no-repeat',
        }}
      />
      {/* Same readability gradient as Home. */}
      <div
        aria-hidden
        className="absolute inset-0 z-0"
        style={{
          background:
            'linear-gradient(180deg, rgba(0,0,0,0.05) 0%, rgba(0,0,0,0.16) 40%, rgba(0,0,0,0.44) 78%, rgba(0,0,0,0.70) 100%)',
        }}
      />

      <div className="relative z-10 px-4 pt-3">
        {/* ---------- Header ---------- */}
        <div className="kpn-row kpn-row--hub">
          <PremiumBackButton testid="play-hub-back-btn" onClick={() => (onBack ? onBack() : navigate('/home'))} />
          <div className="kpn-center flex items-center justify-center gap-2">
            <img
              src={LOGO_ASSET}
              alt="Kickard"
              className="w-10 h-10 object-contain"
              style={{ filter: 'drop-shadow(0 2px 6px rgba(0,0,0,0.55))' }}
            />
            <div
              className="font-heading tracking-[0.22em] text-white"
              style={{ fontSize: 22, lineHeight: 1 }}
            >
              PLAY
            </div>
          </div>
          <span className="kpn-end"><PremiumHomeButton testid="play-hub-home-btn" /></span>
        </div>

        {/* ---------- Choose your mode ---------- */}
        <div
          className="text-center font-heading mt-3 mb-4"
          style={{
            color: 'hsl(var(--k-gold-bright, 46 85% 60%))',
            letterSpacing: '0.32em',
            fontSize: 12,
          }}
        >
          CHOOSE YOUR MODE
        </div>

        {/* ---------- Mode cards ---------- */}
        <ModeCard
          testId="mode-quick-card"
          art={CARD.quick}
          title="QUICK MATCH"
          lines={['INSTANT MATCHMAKING', 'CASUAL']}
          borderClass="border-[rgba(255,215,120,0.35)]"
          onClick={onQuickTap}
        />

        <ModeCard
          testId="mode-ranked-card"
          art={CARD.ranked}
          title="RANKED"
          lines={rankedClosedExplicit ? ['OPENS SOON'] : ['PRESEASON', 'SR & WEEKLY CUP']}
          borderClass="border-[rgba(255,120,90,0.35)]"
          disabled={rankedClosedExplicit}
          onClick={onRankedTap}
        />

        {/* ---------- Friend Match (inline accordion) ---------- */}
        <FriendCard
          expanded={friendExpanded}
          onToggle={() => {
            setFriendExpanded((v) => !v);
            if (friendExpanded) setCodeOpen(false);
            track(EVT.HOME_CTA_CLICKED, { metadata: { cta: 'play_hub_friend_toggle' } });
          }}
          onCreate={onFriendCreate}
          onCodeTap={onCodeTap}
          onJoin={onFriendJoin}
          codeOpen={codeOpen}
          joinCode={joinCode}
          setJoinCode={setJoinCode}
          codeInputRef={codeInputRef}
          busy={actions.busy}
          error={actions.error}
        />

        <ModeCard
          testId="mode-bot-card"
          art={CARD.bot}
          title="VS BOT"
          lines={['PRACTICE · OFFLINE', 'NO SR']}
          borderClass="border-[rgba(80,160,255,0.4)]"
          onClick={onBotTap}
        />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------
// ModeCard — single tap-row with full-bleed WebP art + overlay text.
// ---------------------------------------------------------------
function ModeCard({ testId, art, title, lines, borderClass = '', onClick, disabled = false }) {
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      aria-disabled={disabled}
      className={
        'relative w-full mb-3 overflow-hidden rounded-[14px] border-2 text-left ' +
        borderClass + ' ' +
        (disabled ? 'opacity-50 cursor-not-allowed' : 'active:translate-y-[1px]')
      }
      style={{
        aspectRatio: '358 / 110',
        backgroundColor: '#000',
      }}
    >
      <img
        src={art}
        alt=""
        aria-hidden
        className="absolute inset-0 w-full h-full object-cover"
        draggable={false}
      />
      {/* Right-side darkening for text legibility */}
      <div
        aria-hidden
        className="absolute inset-0"
        style={{
          background:
            'linear-gradient(90deg, rgba(0,0,0,0) 24%, rgba(0,0,0,0.35) 60%, rgba(0,0,0,0.55) 100%)',
        }}
      />
      <div className="relative h-full flex items-center justify-between px-4">
        <div className="flex-1 min-w-0 pl-[32%]">
          <div
            className="font-heading text-white"
            style={{ fontSize: 'clamp(16px, 5.2vw, 20px)', letterSpacing: '0.08em', lineHeight: 1.1 }}
          >
            {title}
          </div>
          <div className="mt-1 font-mono text-white/80" style={{ fontSize: 'clamp(10px, 2.8vw, 11px)', letterSpacing: '0.14em' }}>
            {lines.map((l) => (<div key={l}>{l}</div>))}
          </div>
        </div>
        <Chevron direction="right" />
      </div>
    </button>
  );
}

// ---------------------------------------------------------------
// FriendCard — inline accordion. Collapsed matches the Friend row art;
// expanded reveals three sub-action rows (Create / Enter Code / Join).
// ---------------------------------------------------------------
function FriendCard({
  expanded, onToggle, onCreate, onCodeTap, onJoin,
  codeOpen, joinCode, setJoinCode, codeInputRef, busy, error,
}) {
  const glow = expanded
    ? '0 0 0 2px rgba(255,205,105,0.95), 0 0 36px 6px rgba(255,180,60,0.45)'
    : '0 0 0 1.5px rgba(255,205,105,0.65), 0 0 16px 2px rgba(255,180,60,0.25)';
  return (
    <div
      data-testid="mode-friend-card"
      className="relative w-full mb-3 rounded-[14px] overflow-hidden"
      style={{
        background: '#000',
        boxShadow: glow,
      }}
    >
      {/* Toggle row — same visual language as other mode cards */}
      <button
        type="button"
        data-testid="mode-friend-toggle"
        onClick={onToggle}
        className="relative w-full text-left active:translate-y-[1px]"
        style={{ aspectRatio: '358 / 110', display: 'block' }}
      >
        <img
          src={CARD.friend}
          alt=""
          aria-hidden
          className="absolute inset-0 w-full h-full object-cover"
          draggable={false}
        />
        <div
          aria-hidden
          className="absolute inset-0"
          style={{
            background:
              'linear-gradient(90deg, rgba(0,0,0,0) 24%, rgba(0,0,0,0.35) 60%, rgba(0,0,0,0.55) 100%)',
          }}
        />
        <div className="relative h-full flex items-center justify-between px-4">
          <div className="flex-1 min-w-0 pl-[32%]">
            <div
              className="font-heading text-white"
              style={{ fontSize: 'clamp(16px, 5.2vw, 20px)', letterSpacing: '0.08em', lineHeight: 1.1 }}
            >
              FRIEND MATCH
            </div>
            <div className="mt-1 font-mono text-white/85" style={{ fontSize: 'clamp(10px, 2.8vw, 11px)', letterSpacing: '0.14em' }}>
              PLAY SOMEONE YOU KNOW
            </div>
          </div>
          <Chevron direction={expanded ? 'up' : 'right'} />
        </div>
      </button>

      {expanded && (
        <div className="px-3 pt-2 pb-3 flex flex-col gap-2 relative z-10">
          <SubActionRow
            testId="mode-friend-create-btn"
            icon={ICON.create}
            title="CREATE ROOM"
            lines={['CREATE A PRIVATE ROOM', 'AND INVITE YOUR FRIEND']}
            onClick={onCreate}
            disabled={busy}
            busyLabel={busy ? 'CREATING…' : null}
          />
          <SubActionRow
            testId="mode-friend-code-row"
            icon={ICON.code}
            title="ENTER ROOM CODE"
            lines={['JOIN A ROOM WITH A CODE']}
            onClick={onCodeTap}
            disabled={busy}
            active={codeOpen}
          />
          {codeOpen && (
            <input
              ref={codeInputRef}
              data-testid="mode-friend-code-input"
              value={joinCode}
              onChange={(e) => setJoinCode(
                e.target.value.toUpperCase().replace(/[^A-Z2-9]/g, '').slice(0, 6),
              )}
              placeholder="ROOM CODE"
              maxLength={6}
              autoCapitalize="characters"
              autoComplete="off"
              spellCheck={false}
              disabled={busy}
              className="w-full px-3 py-2.5 rounded-[10px] bg-black/70 text-white text-center font-mono outline-none disabled:opacity-50"
              style={{
                fontSize: 20,
                letterSpacing: '0.32em',
                border: '1.5px solid rgba(255,205,105,0.6)',
                boxShadow: '0 0 10px rgba(255,180,60,0.25)',
              }}
            />
          )}
          <SubActionRow
            testId="mode-friend-join-btn"
            icon={ICON.join}
            title="JOIN ROOM"
            lines={['JOIN A ROOM FROM A LINK', 'OR INVITE']}
            onClick={onJoin}
            disabled={busy || joinCode.length !== 6}
            busyLabel={busy ? 'JOINING…' : null}
          />
          {error && (
            <div
              data-testid="mode-friend-error"
              className="text-red-300 text-center text-sm px-2 pt-1"
            >
              {error}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------
// SubActionRow — Friend-expanded tap row with icon + title + subtitle.
// ---------------------------------------------------------------
function SubActionRow({ testId, icon, title, lines, onClick, disabled = false, active = false, busyLabel = null }) {
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      className={
        'relative w-full text-left rounded-[10px] overflow-hidden ' +
        (disabled ? 'opacity-55 cursor-not-allowed' : 'active:translate-y-[1px]')
      }
      style={{
        background: 'rgba(10,10,12,0.72)',
        border: active
          ? '1.5px solid rgba(255,205,105,0.95)'
          : '1px solid rgba(255,205,105,0.35)',
        boxShadow: active ? '0 0 14px rgba(255,180,60,0.4)' : 'none',
        padding: '10px 12px',
      }}
    >
      <div className="flex items-center gap-3">
        <img
          src={icon}
          alt=""
          aria-hidden
          className="shrink-0"
          style={{ width: 44, height: 44, objectFit: 'contain' }}
          draggable={false}
        />
        <div className="flex-1 min-w-0">
          <div
            className="font-heading text-white"
            style={{ fontSize: 'clamp(14px, 4.2vw, 16px)', letterSpacing: '0.08em', lineHeight: 1.1 }}
          >
            {busyLabel || title}
          </div>
          <div
            className="mt-0.5 font-mono text-white/70"
            style={{ fontSize: 'clamp(9px, 2.6vw, 10px)', letterSpacing: '0.14em' }}
          >
            {lines.map((l) => (<div key={l}>{l}</div>))}
          </div>
        </div>
        <Chevron direction="right" />
      </div>
    </button>
  );
}

function Chevron({ direction = 'right' }) {
  const rotate = direction === 'up' ? -90 : 0;
  return (
    <svg
      viewBox="0 0 24 24"
      aria-hidden
      focusable="false"
      style={{
        width: 20,
        height: 20,
        flexShrink: 0,
        marginLeft: 10,
        transform: `rotate(${rotate}deg)`,
        transition: 'transform 180ms ease',
      }}
    >
      <path
        d="M9 6 L15 12 L9 18"
        fill="none"
        stroke="hsl(46, 85%, 58%)"
        strokeWidth="2.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
